/**
 * BookingPage.jsx
 * Multi-step booking wizard: Date → Review → Payment → Confirm
 * Route: /book/:vin
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../context/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const STEPS = ['Dates', 'Review', 'Payment'];

// Loaded once per app load; publishable key is not secret and is safe to embed at build time.
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

function formatCents(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

function calcDays(start, end) {
  if (!start || !end) return 0;
  const ms = new Date(end) - new Date(start);
  return Math.max(1, Math.ceil(ms / 86400000));
}

// Require some lead time before pickup so the booking doesn't go stale mid-checkout.
const MIN_LEAD_MINUTES = 30;
function minPickupLocal() {
  return new Date(Date.now() + MIN_LEAD_MINUTES * 60000).toISOString().slice(0, 16);
}

export default function BookingPage() {
  const { vin }    = useParams();
  const navigate   = useNavigate();
  const api        = useApi();

  const [step, setStep]         = useState(0);
  const [vehicle, setVehicle]   = useState(null);
  const [loyalty, setLoyalty]   = useState(null);
  const [startDate, setStart]   = useState('');
  const [endDate, setEnd]       = useState('');
  const [notes, setNotes]       = useState('');
  const [redeemPts, setRedeem]  = useState(0);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [quote, setQuote]         = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError]     = useState('');
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [processingStage, setProcessingStage] = useState('');
  const [deliveryMode, setDeliveryMode]           = useState('none'); // 'none' | 'dedicated' | 'custom'
  const [deliveryLocationId, setDeliveryLocationId] = useState('');
  const [deliveryAddress, setDeliveryAddress]       = useState('');
  const [deliveryLocations, setDeliveryLocations]   = useState([]);

  // Load vehicle + loyalty
  useEffect(() => {
    api.get(`/vehicles/${vin}`).then(r => setVehicle(r.data)).catch(() => setError('Vehicle not found'));
    api.get('/users/me/loyalty').then(r => setLoyalty(r.data)).catch(() => {});
    api.get('/delivery-locations').then(r => setDeliveryLocations(r.data || [])).catch(() => {});
  }, [vin]);

  // Rough estimate shown on the Dates step only — the real, tax-inclusive
  // total (via /bookings/quote) is fetched once the user reaches Review.
  const days      = calcDays(startDate, endDate);
  const dailyRate = vehicle?.dailyRateCents || 0;
  const base      = dailyRate * days;
  const deposit   = quote?.deposit_amount_cents ?? Math.min(dailyRate, 50000);
  const total     = quote?.total_amount_cents ?? base;
  const isPickupTooSoon = !!startDate && new Date(startDate).getTime() < Date.now() + MIN_LEAD_MINUTES * 60000;
  const maxRedeem = Math.max(0, Math.min(
    quote?.loyalty_points_available || 0,
    (quote?.base_price_cents || 0) - (quote?.discount_cents || 0) - 50
  ));

  // Fetch the authoritative price quote (tax + loyalty discount/points
  // included) whenever the Review step's inputs change.
  useEffect(() => {
    if (step !== 1 || !startDate || !endDate) return;
    // Don't fire (or error) until the delivery selection is actually
    // complete — otherwise picking "dedicated" shows a false pricing error
    // before a location is chosen, same for "custom" before an address is typed.
    if (deliveryMode === 'dedicated' && !deliveryLocationId) { setQuote(null); setQuoteError(''); return; }
    if (deliveryMode === 'custom' && !deliveryAddress.trim()) { setQuote(null); setQuoteError(''); return; }
    let cancelled = false;
    setQuoteLoading(true); setQuoteError('');
    const timer = setTimeout(() => {
      api.post('/bookings/quote', {
        vin,
        start_time:     new Date(startDate).toISOString(),
        end_time:       new Date(endDate).toISOString(),
        loyalty_points: redeemPts,
        extras:         { delivery: deliveryMode !== 'none' },
        delivery_mode:  deliveryMode,
        delivery_location_id: deliveryMode === 'dedicated' ? deliveryLocationId : undefined,
        delivery_address:     deliveryMode === 'custom' ? deliveryAddress : undefined,
      })
        .then(r => { if (!cancelled) setQuote(r.data); })
        .catch(err => {
          if (cancelled) return;
          const msg = err.response?.data?.error;
          setQuoteError(typeof msg === 'string' ? msg : 'Could not load pricing. Please try again.');
        })
        .finally(() => { if (!cancelled) setQuoteLoading(false); });
    }, 400); // debounce redeem-points slider drags / address typing
    return () => { cancelled = true; clearTimeout(timer); };
  }, [step, startDate, endDate, redeemPts, vin, deliveryMode, deliveryLocationId, deliveryAddress]);

  const handleBookError = (err) => {
    const msg = err.response?.data?.error;
    if (typeof msg === 'object' && msg.error === 'verification_required') {
      navigate('/verify?reason=private-rentals');
    } else if (typeof msg === 'object' && msg.error === 'documents_expired') {
      setError(msg.message || 'Your documents on file have expired. Please re-upload.');
    } else {
      setError(typeof msg === 'string' ? msg : (err.message || 'Booking failed. Please try again.'));
    }
  };

  const continueToReview = async () => {
    setCheckingAvailability(true);
    setError('');
    try {
      const { data } = await api.post('/bookings/quote', {
        vin,
        start_time: new Date(startDate).toISOString(),
        end_time: new Date(endDate).toISOString(),
        loyalty_points: redeemPts,
        extras: { delivery: false },
        delivery_mode: 'none',
      });
      setQuote(data);
      setQuoteError('');
      setStep(1);
    } catch (err) {
      const message = err.response?.data?.error;
      setError(typeof message === 'string' ? message : 'Could not verify availability. Please try again.');
    } finally {
      setCheckingAvailability(false);
    }
  };

  if (!vehicle) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          {vehicle.imageUrl && (
            <img src={vehicle.imageUrl} alt={vehicle.model} className="w-20 h-14 object-cover rounded-lg" />
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {vehicle.year} {vehicle.make} {vehicle.model}
            </h1>
            <p className="text-gray-500 text-sm">{formatCents(dailyRate)}/day</p>
          </div>
        </div>

        {/* Step indicator */}
        <div className="flex items-center mb-8">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                ${i <= step ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                {i + 1}
              </div>
              <span className={`ml-2 text-sm font-medium ${i === step ? 'text-blue-600' : 'text-gray-400'}`}>{s}</span>
              {i < STEPS.length - 1 && <div className="w-8 h-px bg-gray-300 mx-3" />}
            </div>
          ))}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 mb-6">{error}</div>
        )}

        {/* Step 0: Dates */}
        {step === 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-5">
            <h2 className="text-lg font-semibold text-gray-800">Select Dates</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Date & Time</label>
                <input
                  type="datetime-local"
                  value={startDate}
                  min={minPickupLocal()}
                  onChange={e => setStart(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-400 mt-1">Pickup must be at least {MIN_LEAD_MINUTES} minutes from now.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Return Date & Time</label>
                <input
                  type="datetime-local"
                  value={endDate}
                  min={startDate || minPickupLocal()}
                  onChange={e => setEnd(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            {days > 0 && (
              <p className="text-sm text-gray-500">{days} day{days !== 1 ? 's' : ''} · {formatCents(base)} estimated</p>
            )}
            {isPickupTooSoon && (
              <p className="text-sm text-red-600">Pickup time must be at least {MIN_LEAD_MINUTES} minutes from now — please pick a later time.</p>
            )}
            <button
              onClick={continueToReview}
              disabled={!startDate || !endDate || days < 1 || isPickupTooSoon || checkingAvailability}
              className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-40 transition"
            >
              {checkingAvailability ? 'Checking availability…' : 'Continue'}
            </button>
          </div>
        )}

        {/* Step 1: Review */}
        {step === 1 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-5">
            <h2 className="text-lg font-semibold text-gray-800">Review Your Booking</h2>

            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-gray-500">Vehicle</dt><dd className="font-medium">{vehicle.year} {vehicle.make} {vehicle.model}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-500">Pickup</dt><dd className="font-medium">{new Date(startDate).toLocaleString()}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-500">Return</dt><dd className="font-medium">{new Date(endDate).toLocaleString()}</dd></div>
              <div className="flex justify-between"><dt className="text-gray-500">Duration</dt><dd className="font-medium">{quote?.num_days ?? days} day{(quote?.num_days ?? days) !== 1 ? 's' : ''}</dd></div>
            </dl>

            {/* Pickup / delivery location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Pickup / Drop-off</label>
              <div className="space-y-2">
                <label className="flex items-start gap-2 border border-gray-200 rounded-lg p-3 cursor-pointer has-[:checked]:border-blue-500 has-[:checked]:bg-blue-50">
                  <input type="radio" className="mt-0.5" name="deliveryMode" checked={deliveryMode === 'none'}
                    onChange={() => setDeliveryMode('none')} />
                  <span className="text-sm">
                    <span className="font-medium text-gray-800">Pick up at the vehicle's location</span>
                    <span className="block text-gray-500 text-xs mt-0.5">
                      {vehicle.homeAddress ? `${vehicle.homeAddress}, ${vehicle.homeCity || ''}` : 'Free'} · Free
                    </span>
                  </span>
                </label>

                {deliveryLocations.length > 0 && (
                  <label className="flex items-start gap-2 border border-gray-200 rounded-lg p-3 cursor-pointer has-[:checked]:border-blue-500 has-[:checked]:bg-blue-50">
                    <input type="radio" className="mt-0.5" name="deliveryMode" checked={deliveryMode === 'dedicated'}
                      onChange={() => setDeliveryMode('dedicated')} />
                    <span className="text-sm flex-1">
                      <span className="font-medium text-gray-800">Deliver to a common location</span>
                      {deliveryMode === 'dedicated' && (
                        <>
                          <select
                            value={deliveryLocationId}
                            onChange={e => setDeliveryLocationId(e.target.value)}
                            className="mt-2 w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                          >
                            <option value="">Select a location…</option>
                            {deliveryLocations.map(l => (
                              <option key={l.id} value={l.id}>
                                {l.name} — {l.priceCents > 0 ? formatCents(l.priceCents) : 'Free'}
                              </option>
                            ))}
                          </select>
                          {!deliveryLocationId && (
                            <span className="block text-xs text-gray-400 mt-1">Select a location above to see pricing.</span>
                          )}
                        </>
                      )}
                    </span>
                  </label>
                )}

                <label className="flex items-start gap-2 border border-gray-200 rounded-lg p-3 cursor-pointer has-[:checked]:border-blue-500 has-[:checked]:bg-blue-50">
                  <input type="radio" className="mt-0.5" name="deliveryMode" checked={deliveryMode === 'custom'}
                    onChange={() => setDeliveryMode('custom')} />
                  <span className="text-sm flex-1">
                    <span className="font-medium text-gray-800">Deliver to my address</span>
                    <span className="block text-gray-500 text-xs mt-0.5">Mileage-based delivery fee from the vehicle's home base.</span>
                    {deliveryMode === 'custom' && (
                      <>
                        <input
                          type="text"
                          value={deliveryAddress}
                          onChange={e => setDeliveryAddress(e.target.value)}
                          placeholder="Street address, city, state, zip"
                          className="mt-2 w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                        />
                        {deliveryAddress.trim() && quoteLoading && (
                          <span className="flex items-center gap-1.5 text-xs text-gray-400 mt-1">
                            <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-400" />
                            Looking up this address…
                          </span>
                        )}
                        {deliveryAddress.trim() && !quoteLoading && quote?.delivery_details?.mode === 'custom' && (
                          <span className="block text-xs text-green-700 mt-1">
                            ✓ Delivery available to {quote.delivery_details.matchedAddress}
                          </span>
                        )}
                        {deliveryAddress.trim() && !quoteLoading && quoteError && (
                          <span className="block text-xs text-red-600 mt-1">Couldn't verify that address — check for typos.</span>
                        )}
                      </>
                    )}
                  </span>
                </label>
              </div>
            </div>

            {quoteError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{quoteError}</div>
            )}

            {quoteLoading && !quote && (
              <div className="animate-pulse space-y-2">
                <div className="h-4 bg-gray-100 rounded w-full" />
                <div className="h-4 bg-gray-100 rounded w-2/3" />
                <div className="h-4 bg-gray-100 rounded w-1/2" />
              </div>
            )}

            {quote && (
              <dl className="space-y-2 text-sm">
                <div className="border-t pt-2 flex justify-between"><dt className="text-gray-500">Base price</dt><dd>{formatCents(quote.base_price_cents)}</dd></div>
                {quote.discount_cents > 0 && (
                  <div className="flex justify-between text-green-600">
                    <dt>Loyalty discount ({quote.loyalty_tier})</dt>
                    <dd>−{formatCents(quote.discount_cents)}</dd>
                  </div>
                )}
                {quote.loyalty_points_redeemed > 0 && (
                  <div className="flex justify-between text-green-600">
                    <dt>Points redeemed ({quote.loyalty_points_redeemed} pts)</dt>
                    <dd>−{formatCents(quote.loyalty_points_redeemed)}</dd>
                  </div>
                )}
                {quote.extras_cents > 0 && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500">Delivery fee</dt>
                    <dd>{formatCents(quote.extras_cents)}</dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt className="text-gray-500">Sales tax ({quote.sales_tax_pct}%{quote.tax_county ? `, ${quote.tax_county} County` : ''})</dt>
                  <dd>{formatCents(quote.tax_cents)}</dd>
                </div>
              </dl>
            )}

            {/* Loyalty tier + points transparency — shown even at bronze/0 so the
                feature is visibly working, not just when there's a discount. */}
            <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600 flex items-center justify-between">
              <span>Loyalty tier: <span className="font-semibold capitalize">{quote?.loyalty_tier || loyalty?.tier || 'bronze'}</span></span>
              {quote?.loyalty_points_to_earn > 0 && (
                <span>You'll earn <span className="font-semibold text-blue-700">+{quote.loyalty_points_to_earn} pts</span> on this booking</span>
              )}
            </div>

            {/* Loyalty points redemption */}
            {(quote?.loyalty_points_available || 0) > 0 && (
              <div className="bg-blue-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-blue-800 mb-2">
                  Redeem Points ({quote.loyalty_points_available} available · 100 pts = $1)
                </label>
                <input
                  type="range"
                  min={0}
                  max={maxRedeem}
                  step={100}
                  value={redeemPts}
                  onChange={e => setRedeem(Number(e.target.value))}
                  className="w-full"
                />
                <p className="text-xs text-blue-700 mt-1">
                  Redeeming {redeemPts} pts = −{formatCents(Math.min(redeemPts, maxRedeem))}
                </p>
              </div>
            )}

            <div className="border-t pt-3 flex justify-between text-base font-bold">
              <span>Total</span>
              <span>{quote ? formatCents(quote.total_amount_cents) : '—'}</span>
            </div>
            <p className="text-xs text-gray-400">
              + {formatCents(deposit)} security deposit (held, not charged unless damage)
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={2}
                placeholder="Any special requests or notes…"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex gap-3">
              <button onClick={() => setStep(0)} className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 transition">Back</button>
              <button
                onClick={() => setStep(2)}
                disabled={!quote || quoteLoading}
                className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-40 transition"
              >
                Continue to Payment
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Payment */}
        {step === 2 && (
          <Elements stripe={stripePromise}>
            <PaymentStep
              total={total}
              deposit={deposit}
              loading={loading}
              processingStage={processingStage}
              onBack={() => setStep(1)}
              onPay={async (stripe, elements) => {
                setLoading(true); setError('');
                let createdBookingId = null;
                try {
                  if (new Date(startDate).getTime() <= Date.now()) {
                    throw new Error('Your pickup time has already passed while completing checkout. Please go back and pick a new time.');
                  }

                  setProcessingStage('Creating your booking…');
                  const { data } = await api.post('/bookings', {
                    vin,
                    start_time:     new Date(startDate).toISOString(),
                    end_time:       new Date(endDate).toISOString(),
                    notes,
                    loyalty_points: redeemPts,
                    extras:         { delivery: deliveryMode !== 'none' },
                    delivery_mode:  deliveryMode,
                    delivery_location_id: deliveryMode === 'dedicated' ? deliveryLocationId : undefined,
                    delivery_address:     deliveryMode === 'custom' ? deliveryAddress : undefined,
                  });
                  createdBookingId = data.booking_id;

                  setProcessingStage('Verifying your card…');
                  const cardElement = elements.getElement(CardElement);
                  const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
                    type: 'card',
                    card: cardElement,
                  });
                  if (pmError) throw new Error(pmError.message);

                  setProcessingStage('Charging your card…');
                  // setup_future_usage attaches this PaymentMethod to the Stripe
                  // Customer so it can be reused below for the deposit hold —
                  // without it, Stripe rejects the second confirmation with
                  // "PaymentMethod ... may not be used again".
                  const { error: rentalError } = await stripe.confirmCardPayment(
                    data.payment_intent_client_secret,
                    { payment_method: paymentMethod.id, setup_future_usage: 'off_session' }
                  );
                  if (rentalError) throw new Error(rentalError.message);

                  if (data.deposit_intent_client_secret) {
                    setProcessingStage('Placing your security deposit hold…');
                    const { error: depositError } = await stripe.confirmCardPayment(
                      data.deposit_intent_client_secret,
                      { payment_method: paymentMethod.id }
                    );
                    if (depositError) throw new Error(depositError.message);
                  }

                  setProcessingStage('Finalizing your reservation…');
                  navigate(`/booking/${data.booking_id}/confirmation`);
                } catch (err) {
                  // Release the booking we just created so it doesn't keep blocking
                  // these dates for future attempts (payment never completed).
                  if (createdBookingId) {
                    api.delete(`/bookings/${createdBookingId}`).catch(() => {});
                  }
                  handleBookError(err);
                } finally {
                  setLoading(false);
                  setProcessingStage('');
                }
              }}
            />
          </Elements>
        )}
      </div>
    </div>
  );
}

// ── Step 2: Payment (must live inside <Elements> to use useStripe/useElements) ─

function PaymentStep({ total, deposit, loading, processingStage, onBack, onPay }) {
  const stripe   = useStripe();
  const elements = useElements();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    await onPay(stripe, elements);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-5">
      <h2 className="text-lg font-semibold text-gray-800">Payment</h2>

      {loading && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent shrink-0" />
          <div>
            <p className="text-sm font-medium text-blue-800">{processingStage || 'Processing…'}</p>
            <p className="text-xs text-blue-600 mt-0.5">This can take up to 30 seconds — please don't refresh or close this tab.</p>
          </div>
        </div>
      )}

      <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-1">
        <div className="flex justify-between font-bold text-base">
          <span>Total due today</span>
          <span>{formatCents(total)}</span>
        </div>
        <div className="flex justify-between text-gray-500">
          <span>Security deposit (hold)</span>
          <span>{formatCents(deposit)}</span>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Card details</label>
        <div className="border border-gray-300 rounded-lg px-3 py-3 focus-within:ring-2 focus-within:ring-blue-500">
          <CardElement options={{ style: { base: { fontSize: '14px' } } }} />
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        <p className="font-medium mb-1">💳 Stripe Payment</p>
        <p>You'll be charged {formatCents(total)} and a {formatCents(deposit)} hold will be placed on your card (released after clean return).</p>
      </div>

      <p className="text-xs text-gray-400">
        By confirming, you agree to the{' '}
        <a href="/rental-policies" className="text-blue-600 hover:underline">rental policies</a>.
        A rental agreement will be emailed to you for signature.
      </p>

      <div className="flex gap-3">
        <button type="button" onClick={onBack} disabled={loading} className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-50 disabled:opacity-40 transition">Back</button>
        <button
          type="submit"
          disabled={loading || !stripe}
          className="flex-1 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 transition"
        >
          {loading ? (processingStage || 'Processing…') : `Confirm & Pay ${formatCents(total)}`}
        </button>
      </div>
    </form>
  );
}
